import { parseISO } from 'date-fns';
import { z } from 'zod';

import { TELEHEALTH_CONTACT_TYPE } from '@/lib/constants';
import type { SchedulingSlot, SchedulingSlotDateTime } from '@/types/scheduling.type';

const schedulingSlotConflictPreferenceSchema = z.enum(['phone', 'video', 'random']);

export type SchedulingSlotConflictPreference = z.infer<
  typeof schedulingSlotConflictPreferenceSchema
>;

/**
 * Normalizes a PostHog multivariate variant into a conflict preference.
 * Unknown, boolean, or missing values fall back to `video`.
 *
 * Call only after feature flags have loaded — unread variants are also `undefined` and
 * must not be treated as the video cohort (see AvailabilityCalendar).
 */
export function resolveSchedulingSlotConflictPreference(
  variant: string | boolean | undefined
): SchedulingSlotConflictPreference {
  const parsed = schedulingSlotConflictPreferenceSchema.safeParse(variant);
  return parsed.success ? parsed.data : 'video';
}

type MergeSchedulingSlotsOptions = {
  conflictPreference: SchedulingSlotConflictPreference;
};

/**
 * Merges two scheduling responses by calendar day.
 * Duplicate `start` ISO timestamps keep one entry based on `conflictPreference`.
 * When preference is `random`, the winner is stable for a given start time (not Math.random).
 * Slots are sorted by start time.
 */
export function mergeSchedulingSlotsByDate(
  slots: SchedulingSlot[] | undefined,
  additionalSlots: SchedulingSlot[] | undefined,
  { conflictPreference }: MergeSchedulingSlotsOptions
): SchedulingSlot[] {
  const primary = slots ?? [];
  const additional = additionalSlots ?? [];

  const dateSet = new Set<string>();
  for (const row of primary) {
    dateSet.add(row.date);
  }
  for (const row of additional) {
    dateSet.add(row.date);
  }

  const sortedDates = [...dateSet].sort((a, b) => a.localeCompare(b));

  const slotsByDate = new Map(primary.map((row) => [row.date, row.slots]));
  const additionalSlotsByDate = new Map(additional.map((row) => [row.date, row.slots]));

  return sortedDates.map((date) => ({
    date,
    slots: mergeSlotsForSingleDay(
      slotsByDate.get(date) ?? [],
      additionalSlotsByDate.get(date) ?? [],
      conflictPreference
    ),
  }));
}

function mergeSlotsForSingleDay(
  slots: SchedulingSlotDateTime[],
  additionalSlots: SchedulingSlotDateTime[],
  conflictPreference: SchedulingSlotConflictPreference
): SchedulingSlotDateTime[] {
  // First-wins: multiple providers can share a start; keep the earliest list entry
  // (Map(slots.map(...)) would overwrite with the last and change which providerId books).
  const slotsByStart = indexSlotsByStartFirstWins(slots);
  const additionalSlotsByStart = indexSlotsByStartFirstWins(additionalSlots);

  const allStarts = new Set([...slotsByStart.keys(), ...additionalSlotsByStart.keys()]);
  const result: SchedulingSlotDateTime[] = [];

  for (const start of allStarts) {
    const slot = slotsByStart.get(start);
    const additionalSlot = additionalSlotsByStart.get(start);

    // Deduplicate by start timestamp only — never show two slots at the same time.
    if (slot && additionalSlot) {
      result.push(pickSlotForConflict(slot, additionalSlot, conflictPreference));
    } else if (slot) {
      result.push(slot);
    } else if (additionalSlot) {
      result.push(additionalSlot);
    }
  }

  result.sort((a, b) => parseISO(a.start).getTime() - parseISO(b.start).getTime());

  return result;
}

/** Indexes by `start`, keeping the first slot when providers share a timestamp. */
function indexSlotsByStartFirstWins(
  slots: SchedulingSlotDateTime[]
): Map<string, SchedulingSlotDateTime> {
  const byStart = new Map<string, SchedulingSlotDateTime>();
  for (const slot of slots) {
    if (!byStart.has(slot.start)) {
      byStart.set(slot.start, slot);
    }
  }
  return byStart;
}

function pickSlotForConflict(
  slot: SchedulingSlotDateTime,
  additionalSlot: SchedulingSlotDateTime,
  conflictPreference: SchedulingSlotConflictPreference
): SchedulingSlotDateTime {
  if (conflictPreference === 'random') {
    // Deterministic per start time so rememos/refetches don't re-roll contact type.
    return stablePickByKey(slot.start, slot, additionalSlot);
  }

  const preferredContactType =
    conflictPreference === 'phone' ? TELEHEALTH_CONTACT_TYPE.PHONE : TELEHEALTH_CONTACT_TYPE.VIDEO;

  const slotMatches = slot.contactType === preferredContactType;
  const additionalMatches = additionalSlot.contactType === preferredContactType;

  if (slotMatches && !additionalMatches) {
    return slot;
  }

  if (additionalMatches && !slotMatches) {
    return additionalSlot;
  }

  // Neither or both match — keep the primary source entry.
  return slot;
}

/** djb2 parity — same key always picks the same option (pure merge across rememos). */
function stablePickByKey<T>(key: string, a: T, b: T): T {
  let hash = 5381;
  for (let i = 0; i < key.length; i++) {
    hash = (hash * 33) ^ key.charCodeAt(i);
  }
  return (hash & 1) === 0 ? a : b;
}
